import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ReactDOMServer from 'react-dom/server';
import clip from 'text-clipper';

import {
    View,
    Link,
    Button,
    Grid,
    GridColumn,
    css,
    colors,
} from '@fairygodboss/ui';

import { CompanyLogo } from '../../Elements/CompanyLogo/CompanyLogo';
import { logoPropType } from './constants';

const MarkDownBody = styled(View)`
    button {
        color: #982ea9 !important;
        text-decoration: none !important;
    }
    button:hover {
        color: #611d6c !important;
    }

    button:hover:before {
        color: orange !important;
    }

    a,
    a:link,
    a:visited,
    a:active,
    a {
        all: unset;
        color: #a234b2;
        text-decoration: underline;
        border: none;
        cursor: pointer;
    }

    a:hover {
        color: #a234b2;
    }
`;

const Company = ({ companyName, industry, companyUrl, logo }) => (
    <View display="flex">
        <View width="60px" height="60px">
            <CompanyLogo
                src={logo?.src}
                alt={logo?.alt}
                href={`/company/jobs/${companyUrl}`}
            />
        </View>

        <View
            display="flex"
            pl="14px"
            flexDirection="column"
            justifyContent={!industry ? 'center' : undefined}
        >
            <View flexDirection="row" display="flex">
                <View
                    as="p"
                    margin="0"
                    fontSize="16px"
                    fontWeight={700}
                    lineHeight="19px"
                    pr="12px"
                >
                    <Link
                        href={`/company-overview/${companyUrl}`}
                        p="0"
                        color={colors.primary[60]}
                    >
                        {companyName}
                    </Link>
                </View>
            </View>
            {industry?.length ? (
                <View flexDirection="row" display="flex">
                    <View
                        // as="p"
                        margin="0"
                        fontSize="16px"
                        lineHeight="26px"
                        css={{
                            strong: { fontWeight: 599, lineHeight: '26px' },
                        }}
                        pr="5px"
                        color={colors.neutral[100]}
                    >
                        <strong>Industry:</strong> {industry}
                    </View>
                </View>
            ) : undefined}
        </View>
    </View>
);

export const About = ({ about, industry, companyName, companyUrl, logo }) => {
    const [collapsed, setCollapsed] = useState(true);

    const onShowMore = useCallback(
        (e) => {
            e.preventDefault();
            setCollapsed(!collapsed);
        },
        [collapsed]
    );

    useEffect(() => {
        const _button = document.getElementById('SPJ-V2-show-more-html');
        if (_button) {
            _button?.addEventListener('click', onShowMore);
        }
        return () => {
            if (_button) {
                _button?.removeEventListener('click', onShowMore);
            }
        };
    }, [onShowMore]);

    const ShowMoreButton = (
        <Button
            id="SPJ-V2-show-more-html"
            css={css({
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-block',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '16px',
                height: '24px',
                padding: '0 4px',
                textDecoration: 'none !important',
                color: colors.primary[60],
                margin: 0,
                '&:hover': {
                    color: `${colors.primary[80]}`,
                },
                '&:active': {
                    outline: 'none',
                },
            })}
            variant="primaryText"
        >
            Show more
        </Button>
    );

    const ShowMoreButtonString = ReactDOMServer.renderToString(ShowMoreButton);

    const clippedAbout = clip(about, 600 + ShowMoreButtonString.length, {
        html: true,
        indicator: `...${ShowMoreButtonString}`,
    });

    const MarkdownSelector = collapsed ? MarkDownBody : View;

    return (
        <View
            borderBottomStyle="solid"
            borderWidth="1px"
            borderColor={colors.secondary[20]}
            pt="large"
            pb="22px"
        >
            <Grid>
                <GridColumn size={{ md: 10.5 }}>
                    <View
                        data-qa="single-jobs-about"
                        as="h3"
                        fontFamily="Open Sans, sans-serif"
                        fontSize="16px"
                        fontWeight={700}
                        lineHeight="24px"
                        margin="0 0 24px 0"
                        color={colors.neutral[100]}
                    >
                        About the company
                    </View>
                </GridColumn>
            </Grid>
            <Grid>
                <GridColumn size={{ md: 10.5 }}>
                    <Company
                        companyName={companyName}
                        industry={industry}
                        companyUrl={companyUrl}
                        logo={logo}
                    />
                </GridColumn>
            </Grid>

            {about ? (
                <Grid>
                    <GridColumn size={{ md: 10.5 }}>
                        <MarkdownSelector
                            pt="medium"
                            as="div"
                            fontSize="16px"
                            lineHeight="24px"
                            color={colors.neutral[100]}
                            dangerouslySetInnerHTML={{
                                __html: collapsed ? clippedAbout : about,
                            }}
                        />
                    </GridColumn>
                </Grid>
            ) : undefined}
        </View>
    );
};

About.propTypes = {
    about: PropTypes.string,
    industry: PropTypes.string,
    companyName: PropTypes.string,
    companyUrl: PropTypes.string,
    logo: logoPropType,
    breakpoints: PropTypes.shape({
        mobile: PropTypes.shape({
            width: PropTypes.number,
            height: PropTypes.number,
            transforms: PropTypes.shape({
                crop: PropTypes.string,
            }),
        }),
        desktop: PropTypes.shape({
            width: PropTypes.number,
            height: PropTypes.number,
            transforms: PropTypes.shape({
                crop: PropTypes.string,
            }),
        }),
    }),
};

About.defaultProps = {
    about: null,
    industry: null,
    companyName: null,
    companyUrl: null,
    logo: null,
    breakpoints: {
        mobile: {
            width: 60,
            height: 60,
            transforms: { crop: 'limit' },
        },
        desktop: {
            width: 60,
            height: 60,
            transforms: { crop: 'limit' },
        },
    },
};
